import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  TrashIcon,
  PlusIcon,
  PencilIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import crmIntegrationApi, { CrmIntegrationData, CrmProject, CrmPhoneNumber } from '../../services/api/crm-integration.api';

interface CrmIntegrationSetupProps {
  onClose: () => void;
  onSaved?: () => void;
}

export default function CrmIntegrationSetup({ onClose, onSaved }: CrmIntegrationSetupProps) {
  const { t } = useTranslation('alerts');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [existingIntegration, setExistingIntegration] = useState<CrmIntegrationData | null>(null);
  const [apiToken, setApiToken] = useState('');
  const [showTokenField, setShowTokenField] = useState(false);
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [baseUrl, setBaseUrl] = useState('https://crm-api.devskin.com/api/v1');
  const [defaultPhones, setDefaultPhones] = useState<string[]>([]);
  const [newDefaultPhone, setNewDefaultPhone] = useState('');
  const [defaultDiscordChannels, setDefaultDiscordChannels] = useState<string[]>([]);
  const [newDiscordChannel, setNewDiscordChannel] = useState('');

  const [projects, setProjects] = useState<CrmProject[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<CrmPhoneNumber[]>([]);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingPhones, setLoadingPhones] = useState(false);

  useEffect(() => {
    loadExistingIntegration();
  }, []);

  const loadExistingIntegration = async () => {
    try {
      setLoading(true);
      const res = await crmIntegrationApi.get();
      if (res.success && res.data) {
        setExistingIntegration(res.data);
        setProjectId(res.data.project_id || '');
        setProjectName(res.data.project_name || '');
        setPhoneNumberId(res.data.phone_number_id || '');
        setBaseUrl(res.data.base_url || 'https://crm-api.devskin.com/api/v1');
        setDefaultPhones(res.data.default_phones || []);
        setDefaultDiscordChannels(res.data.default_discord_channels || []);
        setConnectionStatus('success');

        // Auto-load projects and phone numbers using stored token
        await loadMyData();
      }
    } catch (error) {
      console.error('Error loading CRM integration:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMyData = async () => {
    try {
      setLoadingProjects(true);
      setLoadingPhones(true);
      const [projRes, phoneRes] = await Promise.all([
        crmIntegrationApi.getMyProjects(),
        crmIntegrationApi.getMyPhoneNumbers(),
      ]);
      if (projRes.success) setProjects(projRes.data || []);
      if (phoneRes.success) setPhoneNumbers(phoneRes.data || []);
    } catch (error) {
      console.error('Error loading CRM data:', error);
    } finally {
      setLoadingProjects(false);
      setLoadingPhones(false);
    }
  };

  const handleTestConnection = async () => {
    if (!apiToken && !existingIntegration) {
      toast.error(t('crm.connectionError'));
      return;
    }

    try {
      setTesting(true);
      const token = apiToken || '';
      if (!token) {
        toast.error(t('crm.apiTokenPlaceholder'));
        return;
      }

      const res = await crmIntegrationApi.testConnection({
        api_token: token,
        base_url: baseUrl,
      });

      if (res.success) {
        setConnectionStatus('success');
        toast.success(t('crm.connectionSuccess'));
        await loadProjects(token);
      } else {
        setConnectionStatus('error');
        toast.error(res.message || t('crm.connectionError'));
      }
    } catch (error: any) {
      setConnectionStatus('error');
      toast.error(error.response?.data?.message || t('crm.connectionError'));
    } finally {
      setTesting(false);
    }
  };

  const loadProjects = async (token?: string) => {
    const tokenToUse = token || apiToken;
    if (!tokenToUse) return;

    try {
      setLoadingProjects(true);
      const res = await crmIntegrationApi.getProjects(tokenToUse, baseUrl);
      if (res.success) {
        setProjects(res.data || []);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoadingProjects(false);
    }
  };

  const loadPhoneNumbers = async (projId?: string, token?: string) => {
    const tokenToUse = token || apiToken;
    const projectToUse = projId || projectId;
    if (!tokenToUse || !projectToUse) return;

    try {
      setLoadingPhones(true);
      const res = await crmIntegrationApi.getPhoneNumbers(tokenToUse, projectToUse, baseUrl);
      if (res.success) {
        setPhoneNumbers(res.data || []);
      }
    } catch (error) {
      console.error('Error loading phone numbers:', error);
    } finally {
      setLoadingPhones(false);
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = e.target.value;
    setProjectId(selectedId);
    const project = projects.find(p => p.id === selectedId);
    setProjectName(project?.name || '');
    if (selectedId) {
      if (apiToken) {
        loadPhoneNumbers(selectedId, apiToken);
      } else if (existingIntegration) {
        // Reload phone numbers via stored token (will reload after save)
        // For now just clear selection since project changed
        setPhoneNumberId('');
      }
    }
  };

  const addDefaultPhone = () => {
    const phone = newDefaultPhone.trim();
    if (!phone) return;
    if (!/^\+\d{10,15}$/.test(phone)) {
      toast.error(t('crm.recipients.phoneHelp'));
      return;
    }
    if (defaultPhones.includes(phone)) return;
    setDefaultPhones(prev => [...prev, phone]);
    setNewDefaultPhone('');
  };

  const removeDefaultPhone = (phone: string) => {
    setDefaultPhones(prev => prev.filter(p => p !== phone));
  };

  const addDiscordChannel = () => {
    const channel = newDiscordChannel.trim();
    if (!channel) return;
    // Discord IDs são snowflakes (17-19 dígitos numéricos), ou prefixados com "user:" pra DMs
    const cleanChannel = channel.replace(/^user:/, '');
    if (!/^\d{17,20}$/.test(cleanChannel)) {
      toast.error(t('crm.defaultDiscord.idHelp', 'ID do canal/usuário Discord deve ser um snowflake (17-20 dígitos). Use "user:ID" pra DM.'));
      return;
    }
    if (defaultDiscordChannels.includes(channel)) return;
    setDefaultDiscordChannels(prev => [...prev, channel]);
    setNewDiscordChannel('');
  };

  const removeDiscordChannel = (channel: string) => {
    setDefaultDiscordChannels(prev => prev.filter(c => c !== channel));
  };

  const handleSave = async () => {
    if (!apiToken && !existingIntegration) {
      toast.error(t('crm.apiTokenPlaceholder'));
      return;
    }
    if (!projectId) {
      toast.error(t('crm.selectProject'));
      return;
    }

    try {
      setSaving(true);
      const payload: any = {
        project_id: projectId,
        project_name: projectName,
        base_url: baseUrl,
        phone_number_id: phoneNumberId || undefined,
        default_phones: defaultPhones,
        default_discord_channels: defaultDiscordChannels,
      };

      if (apiToken) {
        payload.api_token = apiToken;
      }

      await crmIntegrationApi.save(payload);

      toast.success(t('crm.saveSuccess'));
      onSaved?.();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('crm.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await crmIntegrationApi.delete();
      toast.success(t('crm.deleteSuccess'));
      setExistingIntegration(null);
      setApiToken('');
      setProjectId('');
      setProjectName('');
      setPhoneNumberId('');
      setDefaultPhones([]);
      setConnectionStatus('idle');
      setProjects([]);
      setPhoneNumbers([]);
      setShowTokenField(false);
      onSaved?.();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('crm.saveError'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full p-8 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  const isEditing = !!existingIntegration;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-gray-900 dark:to-gray-800 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl">
              {isEditing ? (
                <PencilIcon className="h-6 w-6 text-white" />
              ) : (
                <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              )}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {isEditing ? 'Editar Integracao CRM' : t('crm.configure')}
              </h2>
              {isEditing && (
                <span className="text-xs text-green-600 dark:text-green-400 font-medium flex items-center space-x-1">
                  <CheckCircleIcon className="h-3 w-3" />
                  <span>{t('crm.configured')}</span>
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-white/50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5 overflow-y-auto flex-1">
          {/* Connection Status */}
          {connectionStatus !== 'idle' && (
            <div className={`flex items-center space-x-2 p-3 rounded-xl text-sm font-medium ${
              connectionStatus === 'success'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
            }`}>
              {connectionStatus === 'success' ? (
                <CheckCircleIcon className="h-5 w-5" />
              ) : (
                <ExclamationCircleIcon className="h-5 w-5" />
              )}
              <span>
                {connectionStatus === 'success' ? t('crm.configured') : t('crm.notConfigured')}
              </span>
            </div>
          )}

          {/* API Token - collapsed when editing with existing token */}
          {isEditing && !showTokenField ? (
            <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
              <div className="flex items-center space-x-2">
                <KeyIcon className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Token: <span className="font-mono">{existingIntegration?.api_token_masked}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setShowTokenField(true)}
                className="text-xs px-3 py-1.5 text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors font-medium"
              >
                Alterar token
              </button>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('crm.apiToken')} {!isEditing && '*'}
              </label>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder={existingIntegration?.api_token_masked || t('crm.apiTokenPlaceholder')}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              />
              {isEditing && (
                <div className="flex items-center justify-between mt-1">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Deixe em branco para manter o token atual
                  </p>
                  <button
                    type="button"
                    onClick={() => { setShowTokenField(false); setApiToken(''); }}
                    className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                  >
                    Cancelar
                  </button>
                </div>
              )}

              {/* Test Connection Button */}
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={testing || !apiToken}
                className="mt-3 w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                {testing ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    <span>{t('crm.testing')}</span>
                  </>
                ) : (
                  <span>{t('crm.testConnection')}</span>
                )}
              </button>
            </div>
          )}

          {/* Project Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('crm.project')} *
            </label>
            {loadingProjects ? (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Carregando projetos...</span>
              </div>
            ) : (
              <select
                value={projectId}
                onChange={handleProjectChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              >
                <option value="">{t('crm.selectProject')}</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
                {/* If current project not in list, show it */}
                {projectId && !projects.find(p => p.id === projectId) && projectName && (
                  <option value={projectId}>{projectName} (atual)</option>
                )}
              </select>
            )}
          </div>

          {/* Phone Number Selector (for calls) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('crm.phoneNumber')}
            </label>
            {loadingPhones ? (
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Carregando numeros...</span>
              </div>
            ) : (
              <select
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
              >
                <option value="">{t('crm.selectPhoneNumber')}</option>
                {phoneNumbers.map(p => (
                  <option key={p.id} value={p.id}>{p.name ? `${p.name} (${p.number})` : p.number}</option>
                ))}
                {/* If current phone not in list, show it */}
                {phoneNumberId && !phoneNumbers.find(p => p.id === phoneNumberId) && (
                  <option value={phoneNumberId}>{phoneNumberId} (atual)</option>
                )}
              </select>
            )}
          </div>

          {/* Default Phones */}
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('crm.defaultPhones.title')}
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('crm.defaultPhones.description')}
            </p>

            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newDefaultPhone}
                onChange={(e) => setNewDefaultPhone(e.target.value)}
                placeholder={t('crm.recipients.phonePlaceholder')}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDefaultPhone())}
              />
              <button
                type="button"
                onClick={addDefaultPhone}
                className="flex items-center space-x-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
              >
                <PlusIcon className="h-4 w-4" />
                <span>{t('crm.defaultPhones.addPhone')}</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('crm.recipients.phoneHelp')}
            </p>

            {defaultPhones.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                {t('crm.defaultPhones.noPhones')}
              </p>
            ) : (
              <div className="space-y-1">
                {defaultPhones.map(phone => (
                  <div key={phone} className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">{phone}</span>
                    <button
                      type="button"
                      onClick={() => removeDefaultPhone(phone)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Default Discord Channels */}
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('crm.defaultDiscord.title', 'Canais Discord para alertas')}
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {t('crm.defaultDiscord.description', 'IDs de canais Discord (ou user:ID para DM) que receberão os alertas via Discord. O bot precisa estar conectado no CRM e ter acesso ao canal.')}
            </p>

            <div className="flex space-x-2 mb-2">
              <input
                type="text"
                value={newDiscordChannel}
                onChange={(e) => setNewDiscordChannel(e.target.value)}
                placeholder={t('crm.defaultDiscord.placeholder', '1234567890123456789 ou user:1234567890123456789')}
                className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono"
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDiscordChannel())}
              />
              <button
                type="button"
                onClick={addDiscordChannel}
                className="flex items-center space-x-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
              >
                <PlusIcon className="h-4 w-4" />
                <span>{t('crm.defaultDiscord.add', 'Adicionar')}</span>
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              {t('crm.defaultDiscord.idHelp', 'ID do canal/usuário Discord deve ser um snowflake (17-20 dígitos). Use "user:ID" pra DM.')}
            </p>

            {defaultDiscordChannels.length === 0 ? (
              <p className="text-xs text-gray-400 dark:text-gray-500 italic">
                {t('crm.defaultDiscord.noChannels', 'Nenhum canal Discord configurado')}
              </p>
            ) : (
              <div className="space-y-1">
                {defaultDiscordChannels.map(channel => (
                  <div key={channel} className="flex items-center justify-between px-3 py-2 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                    <span className="text-sm text-gray-700 dark:text-gray-300 font-mono">
                      {channel.startsWith('user:') ? `DM → ${channel.slice(5)}` : channel}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeDiscordChannel(channel)}
                      className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 flex-shrink-0">
          <div>
            {isEditing && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors text-sm font-medium"
              >
                <TrashIcon className="h-4 w-4" />
                <span>{deleting ? '...' : t('common:buttons.delete')}</span>
              </button>
            )}
          </div>
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors font-medium"
            >
              {t('common:buttons.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || (!apiToken && !isEditing)}
              className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {saving ? (
                <div className="flex items-center space-x-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  <span>{t('common:messages.loading')}</span>
                </div>
              ) : (
                t('common:buttons.save')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
